import { __ } from '@wordpress/i18n';
import clsx from 'clsx';

import {
	useBlockProps,
	useInnerBlocksProps,
	withColors,
	InspectorControls,
	BlockControls,
	store as blockEditorStore,
	__experimentalGetSpacingClassesAndStyles as useSpacingProps,
} from '@wordpress/block-editor';
import { createBlock, store as blocksStore } from '@wordpress/blocks';
import { useMemo, useRef, useEffect, useState, useCallback } from '@wordpress/element';
import { ToolbarGroup, ToolbarButton } from '@wordpress/components';
import { useSelect, useDispatch } from '@wordpress/data';
import EmblaCarousel from 'embla-carousel';

import CarouselInspectorPanel from './carousel-inspector-panel';
import LayoutStyles from './layout-styles';
import MultiColorControl from './components/multi-color-control';
import SingleColorControl from './components/single-color-control';
import { ARROW_STYLES, PAGINATION_STYLES } from './../utils/carousel-ui';
import {
	resolveCarouselConfig,
	getEffectiveValue,
} from './../utils/resolve-carousel-config';
import { migrateSplideOptions } from './../utils/migrate-splide-options';

import './../editor.scss';

// ---------------------------------------------------------------------------
// Canvas width helpers (mirrors showcase utils)
// ---------------------------------------------------------------------------

function getEditorCanvasWidth() {
	const iframe = document.querySelector( 'iframe[name="editor-canvas"]' );
	if ( iframe?.contentWindow ) {
		return iframe.contentWindow.innerWidth;
	}
	const wrapper = document.querySelector( '.editor-styles-wrapper' );
	return wrapper?.getBoundingClientRect?.().width || window.innerWidth;
}

function getEditorCanvasElement() {
	const iframe = document.querySelector( 'iframe[name="editor-canvas"]' );
	if ( iframe?.contentDocument?.body ) return iframe.contentDocument.body;
	return document.querySelector( '.editor-styles-wrapper' );
}

function formatSpacingValue( value ) {
	if ( value === 0 || value === '0' ) return '0px';
	if ( ! value ) return undefined;
	if ( value.startsWith( 'var(' ) ) return value;
	if ( value.startsWith( 'var:' ) ) {
		const parts = value.replace( 'var:', '' ).split( '|' );
		return `var(--wp--${ parts.join( '--' ) })`;
	}
	return value;
}

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

function Edit( props ) {
	const {
		attributes, setAttributes,
		progressColor, setProgressColor,
		progressBackgroundColor, setProgressBackgroundColor,
		paginationColor, setPaginationColor,
		paginationBackgroundColor, setPaginationBackgroundColor,
		paginationBorderColor, setPaginationBorderColor,
		paginationInactiveColor, setPaginationInactiveColor,
		paginationInactiveBackgroundColor, setPaginationInactiveBackgroundColor,
		paginationInactiveBorderColor, setPaginationInactiveBorderColor,
		arrowsColor, setArrowsColor,
		arrowsBackgroundColor, setArrowsBackgroundColor,
		arrowsBorderColor, setArrowsBorderColor,
		counterColor, setCounterColor,
		isSelected, clientId, className, name,
		__unstableLayoutClassNames: layoutClassNames,
	} = props;

	const { template, paginationStyle, carouselOptions, splideOptions, style } = attributes;
	const slideGap = formatSpacingValue( style?.spacing?.blockGap );

	// ------------------------------------------------------------------
	// Auto-migration: splideOptions → carouselOptions (one-time per block)
	// ------------------------------------------------------------------

	useEffect( () => {
		if ( splideOptions && Object.keys( splideOptions ).length > 0 ) {
			const migrated = migrateSplideOptions( splideOptions );
			setAttributes( { carouselOptions: migrated, splideOptions: undefined } );
		}
	}, [] ); // eslint-disable-line react-hooks/exhaustive-deps

	// ------------------------------------------------------------------
	// Config
	// ------------------------------------------------------------------

	const spacingProps = useSpacingProps( attributes );

	const config = useMemo(
		() => resolveCarouselConfig( carouselOptions ),
		[ carouselOptions ]
	);

	// ------------------------------------------------------------------
	// Canvas width tracking (mirrors static-flow/edit)
	// ------------------------------------------------------------------

	const [ canvasWidth, setCanvasWidth ] = useState( () => getEditorCanvasWidth() );
	const canvasWidthRef = useRef( canvasWidth );
	canvasWidthRef.current = canvasWidth;

	useEffect( () => {
		const updateWidth = () => setCanvasWidth( getEditorCanvasWidth() );
		updateWidth();

		const resizeObserver = new ResizeObserver( updateWidth );
		const target = getEditorCanvasElement();
		if ( target ) resizeObserver.observe( target );

		return () => resizeObserver.disconnect();
	}, [] );

	// ------------------------------------------------------------------
	// Effective values at current canvas width
	// ------------------------------------------------------------------

	const effective = useMemo( () => ( {
		arrows: getEffectiveValue( config, 'arrows', canvasWidth ),
		pagination: getEffectiveValue( config, 'pagination', canvasWidth ),
		counter: getEffectiveValue( config, 'counter', canvasWidth ),
		progressBar: getEffectiveValue( config, 'progressBar', canvasWidth ),
		slideSize: getEffectiveValue( config, 'slideSize', canvasWidth ) || '100%',
		destroy: getEffectiveValue( config, 'destroy', canvasWidth ),
		loop: getEffectiveValue( config, 'loop', canvasWidth ),
		align: getEffectiveValue( config, 'align', canvasWidth ) || 'start',
		slidesToScroll: getEffectiveValue( config, 'slidesToScroll', canvasWidth ) || 1,
		containScroll: getEffectiveValue( config, 'containScroll', canvasWidth ),
	} ), [ config, canvasWidth ] );

	const isDestroyed = !! effective.destroy;

	// ------------------------------------------------------------------
	// Inner blocks
	// ------------------------------------------------------------------

	const { hasSlideVariations } = useSelect(
		( select ) => ( {
			hasSlideVariations: select( blocksStore )
				.getBlockVariations( 'groundworx/slide', 'inserter' ).length > 0,
		} ),
		[]
	);

	const innerBlocks = useSelect(
		( select ) => select( blockEditorStore ).getBlock( clientId )?.innerBlocks || [],
		[ clientId ]
	);

	const slideCount = innerBlocks.length;

	const innerBlocksProps = useInnerBlocksProps(
		{ className: 'gwx-carousel__container' },
		{
			renderAppender: false,
			defaultBlock: { name: 'groundworx/slide' },
			directInsert: ! hasSlideVariations,
			template: [ [ 'groundworx/slide' ] ],
			templateInsertUpdatesSelection: true,
			__unstableDisableLayoutClassNames: true,
			orientation: 'horizontal',
		}
	);

	// ------------------------------------------------------------------
	// Refs & state
	// ------------------------------------------------------------------

	const viewportRef = useRef( null );
	const wrapperRef = useRef( null );
	const emblaRef = useRef( null );
	const lastSelectedSlide = useRef( -1 );

	const [ selectedSnap, setSelectedSnap ] = useState( 0 );
	const [ totalSnaps, setTotalSnaps ] = useState( 0 );
	const [ canScrollPrev, setCanScrollPrev ] = useState( false );
	const [ canScrollNext, setCanScrollNext ] = useState( false );
	const [ hasNoOverflow, setHasNoOverflow ] = useState( false );

	const { insertBlock, selectBlock } = useDispatch( blockEditorStore );

	// ------------------------------------------------------------------
	// Embla initialisation (mirrors flow-slides/edit.js)
	// ------------------------------------------------------------------

	useEffect( () => {
		if ( emblaRef.current ) {
			emblaRef.current.destroy();
			emblaRef.current = null;
		}

		if ( isDestroyed ) return;

		let debounceTimer;
		let resizeObserver;
		let mutationObserver;

		const timer = setTimeout( () => {
			if ( ! viewportRef.current ) return;

			const wantsLoop = !! effective.loop;
			let parsedContainScroll = effective.containScroll;
			if ( parsedContainScroll === 'false' || parsedContainScroll === false ) {
				parsedContainScroll = false;
			}

			const options = {
				loop: wantsLoop,
				align: effective.align,
				slidesToScroll: effective.slidesToScroll,
				containScroll: wantsLoop ? false : ( parsedContainScroll || false ),
				duration: 20,
				watchDrag: false,
				watchSlides: true,
				watchResize: false,
				startIndex: 0,
			};

			try {
				emblaRef.current = EmblaCarousel( viewportRef.current, options );

				const dispatchSelect = () => {
					if ( ! emblaRef.current ) return;
					setSelectedSnap( emblaRef.current.selectedScrollSnap() );
					setTotalSnaps( emblaRef.current.scrollSnapList().length );
					setCanScrollPrev( emblaRef.current.canScrollPrev() );
					setCanScrollNext( emblaRef.current.canScrollNext() );
				};

				emblaRef.current.on( 'select', dispatchSelect );
				emblaRef.current.on( 'init', dispatchSelect );
				emblaRef.current.on( 'reInit', dispatchSelect );

				// Debounced reInit for DOM / size changes
				const debouncedReInit = () => {
					clearTimeout( debounceTimer );
					debounceTimer = setTimeout( () => {
						if ( ! emblaRef.current ) return;
						const idx = emblaRef.current.selectedScrollSnap();
						emblaRef.current.reInit( { startIndex: idx } );
					}, 50 );
				};

				// Observe viewport + container + slides immediately.
				// Must start early: templates like default/overlay use CSS grid
				// that narrows the viewport for arrow columns — if we wait until
				// after the rAF chain the resize has already happened.
				const container = viewportRef.current?.querySelector( '.gwx-carousel__container' );
				if ( container ) {
					resizeObserver = new ResizeObserver( debouncedReInit );
					resizeObserver.observe( viewportRef.current );
					resizeObserver.observe( container );
					container.querySelectorAll( '.gwx-carousel__slide' ).forEach( ( slide ) => {
						resizeObserver.observe( slide );
					} );

					mutationObserver = new MutationObserver( () => {
						resizeObserver.disconnect();
						resizeObserver.observe( viewportRef.current );
						resizeObserver.observe( container );
						container.querySelectorAll( '.gwx-carousel__slide' ).forEach( ( slide ) => {
							resizeObserver.observe( slide );
						} );
						debouncedReInit();
					} );
					mutationObserver.observe( container, {
						childList: true,
						subtree: true,
					} );
				}

				// Double-rAF staging then reInit for accurate measurements
				requestAnimationFrame( () => {
					requestAnimationFrame( () => {
						if ( ! emblaRef.current ) return;

						const engine = emblaRef.current.internalEngine();
						const emblaActuallyLooping = engine.options.loop;
						const currentIndex = emblaRef.current.selectedScrollSnap();

						if ( wantsLoop && ! emblaActuallyLooping ) {
							emblaRef.current.reInit( { startIndex: currentIndex, align: 'start' } );
						} else {
							emblaRef.current.reInit( { startIndex: currentIndex } );
						}

						setTimeout( () => {
							if ( ! emblaRef.current ) return;
							const idx = emblaRef.current.selectedScrollSnap();
							emblaRef.current.reInit( { startIndex: idx } );
						}, 50 );

					} );
				} );
			} catch ( e ) {
				// Silently handle init errors
			}
		}, 100 );

		return () => {
			clearTimeout( timer );
			clearTimeout( debounceTimer );
			resizeObserver?.disconnect();
			mutationObserver?.disconnect();
			if ( emblaRef.current ) {
				emblaRef.current.destroy();
				emblaRef.current = null;
			}
		};
	}, [
		slideCount,
		isDestroyed,
		effective.loop,
		effective.align,
		effective.slidesToScroll,
		effective.containScroll,
		effective.slideSize,
		slideGap,
		canvasWidth,
		JSON.stringify( carouselOptions?.breakpoints || {} ),
	] );

	// ------------------------------------------------------------------
	// Scroll prevention (mirrors flow-slides/edit.js)
	// Prevents WordPress from scrolling the viewport when blocks are selected
	// ------------------------------------------------------------------

	useEffect( () => {
		if ( ! viewportRef.current || isDestroyed ) return;

		const viewport = viewportRef.current;
		let reInitPending = false;

		const preventScroll = () => {
			const hadScroll = viewport.scrollLeft !== 0 || viewport.scrollTop !== 0;
			viewport.scrollLeft = 0;
			viewport.scrollTop = 0;

			if ( hadScroll && emblaRef.current && ! reInitPending ) {
				reInitPending = true;
				requestAnimationFrame( () => {
					reInitPending = false;
					if ( emblaRef.current ) {
						const engine = emblaRef.current.internalEngine();
						if ( engine?.translate?.to ) {
							engine.translate.to( engine.location.get() );
						}
					}
				} );
			}
		};

		viewport.addEventListener( 'scroll', preventScroll );
		return () => viewport.removeEventListener( 'scroll', preventScroll );
	}, [ isDestroyed ] );

	// ------------------------------------------------------------------
	// List View auto-scroll (mirrors flow-slides/edit.js)
	// Scrolls to the selected slide when selection comes from outside the viewport
	// ------------------------------------------------------------------

	useEffect( () => {
		if ( isDestroyed ) return;

		let timeoutId = null;

		const unsubscribe = wp.data.subscribe( () => {
			const { isBlockSelected, hasSelectedInnerBlock, getBlock } =
				wp.data.select( blockEditorStore );

			const block = getBlock( clientId );
			const slides = block?.innerBlocks || [];
			if ( ! slides.length ) return;

			const foundIndex = slides.findIndex(
				( slide ) =>
					isBlockSelected( slide.clientId ) ||
					hasSelectedInnerBlock( slide.clientId, true )
			);

			if ( foundIndex < 0 || foundIndex === lastSelectedSlide.current ) return;

			const focusInViewport = viewportRef.current?.contains( document.activeElement );

			if ( ! focusInViewport ) {
				lastSelectedSlide.current = foundIndex;
				if ( timeoutId ) clearTimeout( timeoutId );

				timeoutId = setTimeout( () => {
					if ( emblaRef.current && viewportRef.current ) {
						viewportRef.current.scrollLeft = 0;
						viewportRef.current.scrollTop = 0;

						const engine = emblaRef.current.internalEngine();
						let targetSnap = foundIndex;

						if ( engine?.slideRegistry ) {
							const snapIndex = engine.slideRegistry.findIndex(
								( slideIndexes ) => slideIndexes.includes( foundIndex )
							);
							if ( snapIndex !== -1 ) targetSnap = snapIndex;
						}

						emblaRef.current.scrollTo( targetSnap, true );
					}
				}, 50 );
			} else {
				lastSelectedSlide.current = foundIndex;
			}
		} );

		return () => {
			unsubscribe();
			if ( timeoutId ) clearTimeout( timeoutId );
		};
	}, [ clientId, isDestroyed ] );

	// ------------------------------------------------------------------
	// Overflow detection (mirrors static-flow/edit)
	// ------------------------------------------------------------------

	const hasColumns = effective.slideSize?.includes( '%' );

	useEffect( () => {
		if ( isDestroyed ) {
			setHasNoOverflow( false );
			return;
		}

		const checkOverflow = () => {
			if ( ! wrapperRef.current ) return;

			const viewport = wrapperRef.current.querySelector( '.gwx-carousel__viewport' );
			const emblaContainer = viewport?.querySelector( '.gwx-carousel__container' );
			const slides = emblaContainer?.querySelectorAll( '.gwx-carousel__slide, .wp-block-groundworx-slide' );

			if ( ! emblaContainer || ! slides?.length ) {
				setHasNoOverflow( false );
				return;
			}

			const viewportSize = viewport.clientWidth;

			let totalSlidesSize = 0;
			slides.forEach( ( slide ) => {
				totalSlidesSize += slide.offsetWidth;
			} );

			// Get gap from first slide's margin
			let gapPx = 0;
			if ( slides.length > 1 ) {
				const slideStyle = getComputedStyle( slides[ 0 ] );
				gapPx = parseFloat( slideStyle.marginInlineEnd ) || 0;
			}

			const totalGaps = gapPx * Math.max( 0, slides.length - 1 );
			const totalContentSize = totalSlidesSize + totalGaps;

			setHasNoOverflow( totalContentSize <= viewportSize );
		};

		// Run checks with delays to catch DOM updates
		const timer1 = setTimeout( checkOverflow, 100 );
		const timer2 = setTimeout( checkOverflow, 300 );
		const timer3 = setTimeout( checkOverflow, 600 );

		const resizeObserver = new ResizeObserver( () => {
			setTimeout( checkOverflow, 50 );
		} );

		let mutationObserver;
		if ( wrapperRef.current ) {
			resizeObserver.observe( wrapperRef.current );

			mutationObserver = new MutationObserver( () => {
				setTimeout( checkOverflow, 50 );
			} );
			mutationObserver.observe( wrapperRef.current, {
				childList: true,
				subtree: true,
			} );
		}

		return () => {
			clearTimeout( timer1 );
			clearTimeout( timer2 );
			clearTimeout( timer3 );
			resizeObserver.disconnect();
			mutationObserver?.disconnect();
		};
	}, [ isDestroyed, slideCount, effective.slideSize, slideGap, canvasWidth ] );

	// ------------------------------------------------------------------
	// No-overflow alignment (mirrors static-flow/edit)
	// ------------------------------------------------------------------

	useEffect( () => {
		const applyAlignment = () => {
			if ( ! wrapperRef.current ) return;

			const emblaContainer = wrapperRef.current.querySelector( '.gwx-carousel__container' );
			if ( ! emblaContainer ) return;

			if ( hasNoOverflow && ! isDestroyed ) {
				emblaContainer.style.justifyContent = 'center';
			} else {
				emblaContainer.style.justifyContent = '';
			}
		};

		const timer1 = setTimeout( applyAlignment, 100 );
		const timer2 = setTimeout( applyAlignment, 300 );

		return () => {
			clearTimeout( timer1 );
			clearTimeout( timer2 );
		};
	}, [ hasNoOverflow, isDestroyed ] );

	// ------------------------------------------------------------------
	// Computed display values
	// ------------------------------------------------------------------

	let columns = 1;
	if ( effective.slideSize?.includes( '%' ) ) {
		const pct = parseFloat( effective.slideSize );
		if ( ! isNaN( pct ) && pct > 0 ) {
			columns = Math.round( 100 / pct );
		}
	}

	// Arrow display
	const arrowStyleName = attributes.arrowStyle || 'chevron';
	const arrowData = ARROW_STYLES[ arrowStyleName ] || ARROW_STYLES.chevron;
	const arrowClasses = clsx( 'gwx-carousel__arrow', 'wp-element-button', {
		'has-text-color': !! attributes.arrowsColor || !! attributes.customArrowsColor,
		'has-background': !! attributes.arrowsBackgroundColor || !! attributes.customArrowsBackgroundColor,
		'has-border-color': !! attributes.arrowsBorderColor || !! attributes.customArrowsBorderColor,
		[ `has-${ attributes.arrowsColor }-color` ]: !! attributes.arrowsColor,
		[ `has-${ attributes.arrowsBackgroundColor }-background-color` ]: !! attributes.arrowsBackgroundColor,
		[ `has-${ attributes.arrowsBorderColor }-border-color` ]: !! attributes.arrowsBorderColor,
	} );
	const arrowInlineStyle = {
		color: attributes.customArrowsColor || undefined,
		backgroundColor: attributes.customArrowsBackgroundColor || undefined,
		borderColor: attributes.customArrowsBorderColor || undefined,
	};

	// Pagination display
	const paginationStyleName = attributes.paginationStyle || 'circleOutline';

	// Snap / progress
	const displaySnaps = totalSnaps || slideCount;
	const displayCurrent = selectedSnap + 1;
	const progressWidth = displaySnaps > 0 ? `${ ( displayCurrent / displaySnaps ) * 100 }%` : '0%';

	// Arrow disabled state
	const isLoop = !! effective.loop;
	const prevDisabled = ! isLoop && ! canScrollPrev;
	const nextDisabled = ! isLoop && ! canScrollNext;

	// ------------------------------------------------------------------
	// Handlers
	// ------------------------------------------------------------------

	const handlePrev = useCallback( () => {
		if ( emblaRef.current ) emblaRef.current.scrollPrev();
	}, [] );

	const handleNext = useCallback( () => {
		if ( emblaRef.current ) emblaRef.current.scrollNext();
	}, [] );

	const addSlide = useCallback( () => {
		const newSlide = createBlock( 'groundworx/slide' );
		const newIndex = innerBlocks.length;
		insertBlock( newSlide, newIndex, clientId );
		selectBlock( newSlide.clientId );

		setTimeout( () => {
			if ( emblaRef.current && ! isDestroyed ) {
				emblaRef.current.reInit();
				setTimeout( () => {
					if ( emblaRef.current ) {
						emblaRef.current.scrollTo( newIndex, false );
					}
				}, 50 );
			}
		}, 150 );
	}, [ innerBlocks.length, clientId, isDestroyed, insertBlock, selectBlock ] );

	// ------------------------------------------------------------------
	// Color controls data
	// ------------------------------------------------------------------

	const arrowColors = [
		{ key: 'text', label: __( 'Text', 'groundworx-carousel' ), value: arrowsColor, setValue: setArrowsColor },
		{ key: 'background', label: __( 'Background', 'groundworx-carousel' ), value: arrowsBackgroundColor, setValue: setArrowsBackgroundColor },
		{ key: 'border', label: __( 'Border', 'groundworx-carousel' ), value: arrowsBorderColor, setValue: setArrowsBorderColor },
	];

	const isNumberPagination = paginationStyleName === 'number';

	// Pagination colors: preset → slug class, custom → inline style.
	const paginationCustomColors = {
		active: {
			color: attributes.customPaginationColor || undefined,
			backgroundColor: isNumberPagination ? ( attributes.customPaginationBackgroundColor || undefined ) : undefined,
			borderColor: isNumberPagination ? ( attributes.customPaginationBorderColor || undefined ) : undefined,
		},
		inactive: {
			color: attributes.customPaginationInactiveColor || attributes.customInactivePaginationColor || undefined,
			backgroundColor: isNumberPagination ? ( attributes.customPaginationInactiveBackgroundColor || undefined ) : undefined,
			borderColor: isNumberPagination ? ( attributes.customPaginationInactiveBorderColor || undefined ) : undefined,
		},
	};

	const activePaginationColors = isNumberPagination
		? [
			{ key: 'text', label: __( 'Text', 'groundworx-carousel' ), value: paginationColor, setValue: setPaginationColor },
			{ key: 'background', label: __( 'Background', 'groundworx-carousel' ), value: paginationBackgroundColor, setValue: setPaginationBackgroundColor },
			{ key: 'border', label: __( 'Border', 'groundworx-carousel' ), value: paginationBorderColor, setValue: setPaginationBorderColor },
		]
		: null;

	const inactivePaginationColors = isNumberPagination
		? [
			{ key: 'text', label: __( 'Text', 'groundworx-carousel' ), value: paginationInactiveColor, setValue: setPaginationInactiveColor },
			{ key: 'background', label: __( 'Background', 'groundworx-carousel' ), value: paginationInactiveBackgroundColor, setValue: setPaginationInactiveBackgroundColor },
			{ key: 'border', label: __( 'Border', 'groundworx-carousel' ), value: paginationInactiveBorderColor, setValue: setPaginationInactiveBorderColor },
		]
		: null;

	const progressColors = [
		{ key: 'foreground', label: __( 'Foreground', 'groundworx-carousel' ), value: progressColor, setValue: setProgressColor },
		{ key: 'background', label: __( 'Background', 'groundworx-carousel' ), value: progressBackgroundColor, setValue: setProgressBackgroundColor },
	];

	function resetAllColors() {
		setArrowsColor( undefined );
		setArrowsBackgroundColor( undefined );
		setArrowsBorderColor( undefined );
		setPaginationColor( undefined );
		setPaginationBackgroundColor( undefined );
		setPaginationBorderColor( undefined );
		setPaginationInactiveColor( undefined );
		setPaginationInactiveBackgroundColor( undefined );
		setPaginationInactiveBorderColor( undefined );
		setProgressColor( undefined );
		setProgressBackgroundColor( undefined );
		setCounterColor( undefined );
	}

	// ------------------------------------------------------------------
	// Block props (mirrors static-flow pattern – CSS vars on the block element)
	// ------------------------------------------------------------------

	const blockProps = useBlockProps( {
		className: clsx(
			className,
			template && `template-${ template }`,
			{ 'is-pagination-number': paginationStyle === 'number' },
			layoutClassNames
		),
		style: {
			...spacingProps.style,
			'--gwx-slide-size': effective.slideSize,
			'--gwx-slide-gap': slideGap || undefined,
			'--gwx-columns': columns,
		},
	} );

	const wrapperClasses = clsx( 'gwx-carousel', 'gwx-carousel--editor', {
		'gwx-carousel--has-arrows': !! effective.arrows,
		'gwx-carousel--has-pagination': !! effective.pagination,
		'gwx-carousel--has-counter': !! effective.counter,
		'gwx-carousel--has-progressbar': !! effective.progressBar,
		'is-destroyed': isDestroyed,
		'is-loop': isLoop,
		'is-overflow': ! isDestroyed && ! hasNoOverflow,
		'is-not-overflow': ! isDestroyed && hasNoOverflow,
		'has-columns': hasColumns,
	} );

	// ------------------------------------------------------------------
	// Render
	// ------------------------------------------------------------------

	return (
		<>
			{ isSelected && (
				<>
					<CarouselInspectorPanel
						attributes={ attributes }
						setAttributes={ setAttributes }
						name={ name }
					/>

					<InspectorControls group="color">
						<MultiColorControl
							label={ __( 'Arrow', 'groundworx-carousel' ) }
							colors={ arrowColors }
							clientId={ clientId }
							resetAllFilter={ resetAllColors }
						/>
						{ isNumberPagination ? (
							<>
								<MultiColorControl
									label={ __( 'Active Pagination', 'groundworx-carousel' ) }
									colors={ activePaginationColors }
									clientId={ clientId }
									resetAllFilter={ resetAllColors }
								/>
								<MultiColorControl
									label={ __( 'Inactive Pagination', 'groundworx-carousel' ) }
									colors={ inactivePaginationColors }
									clientId={ clientId }
									resetAllFilter={ resetAllColors }
								/>
							</>
						) : (
							<>
								<SingleColorControl
									label={ __( 'Active Pagination', 'groundworx-carousel' ) }
									colorValue={ paginationColor }
									setValue={ setPaginationColor }
									clientId={ clientId }
									resetAllFilter={ resetAllColors }
								/>
								<SingleColorControl
									label={ __( 'Inactive Pagination', 'groundworx-carousel' ) }
									colorValue={ paginationInactiveColor }
									setValue={ setPaginationInactiveColor }
									clientId={ clientId }
									resetAllFilter={ resetAllColors }
								/>
							</>
						) }
						<MultiColorControl
							label={ __( 'Progress Bar', 'groundworx-carousel' ) }
							colors={ progressColors }
							clientId={ clientId }
							resetAllFilter={ resetAllColors }
						/>
						<SingleColorControl
							label={ __( 'Counter', 'groundworx-carousel' ) }
							colorValue={ counterColor }
							setValue={ setCounterColor }
							clientId={ clientId }
							resetAllFilter={ resetAllColors }
						/>
					</InspectorControls>
				</>
			) }

			<BlockControls>
				<ToolbarGroup label={ __( 'Slides', 'groundworx-carousel' ) }>
					<ToolbarButton
						variant="primary"
						label={ __( 'Add Slide to Carousel', 'groundworx-carousel' ) }
						onClick={ addSlide }
					>
						{ __( 'Add Slide', 'groundworx-carousel' ) }
					</ToolbarButton>
				</ToolbarGroup>
			</BlockControls>

			<LayoutStyles attributes={ attributes } clientId={ clientId } />

			<section { ...blockProps }>
				<div className={ wrapperClasses }>
					<div className="gwx-carousel__wrapper" ref={ wrapperRef }>
						{ /* Viewport — rendered first in the DOM so the grid
						     resolves the track column size before the auto arrow
						     columns, preventing flex-basis % from becoming
						     indefinite. grid-area placement handles visual order. */ }
						<div
							className="gwx-carousel__viewport"
							ref={ viewportRef }
							tabIndex={ -1 }
							style={ { overflow: 'hidden' } }
						>
							<div { ...innerBlocksProps } />
						</div>

						{ /* Arrows */ }
						{ effective.arrows && (
							<div className="gwx-carousel__arrows">
								<button
									type="button"
									className={ `${ arrowClasses } gwx-carousel__arrow--prev` }
									style={ arrowInlineStyle }
									aria-label={ __( 'Previous slide', 'groundworx-carousel' ) }
									onClick={ handlePrev }
									disabled={ prevDisabled }
								>
									<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
										<path
											d={ arrowData.path }
											transform="scale(-1, 1) translate(-40, 0)"
										/>
									</svg>
								</button>

								{ effective.counter && (
									<div
										className={ clsx( 'gwx-carousel__counter', {
											'has-text-color': !! attributes.counterColor || !! attributes.customCounterColor,
											[ `has-${ attributes.counterColor }-color` ]: !! attributes.counterColor,
										} ) }
										style={ {
											color: attributes.customCounterColor || undefined,
										} }
									>
										<span className="gwx-carousel__counter-current">
											{ displayCurrent }
										</span>
										<span className="gwx-carousel__counter-separator">/</span>
										<span className="gwx-carousel__counter-total">
											{ displaySnaps }
										</span>
									</div>
								) }

								<button
									type="button"
									className={ `${ arrowClasses } gwx-carousel__arrow--next` }
									style={ arrowInlineStyle }
									aria-label={ __( 'Next slide', 'groundworx-carousel' ) }
									onClick={ handleNext }
									disabled={ nextDisabled }
								>
									<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
										<path d={ arrowData.path } />
									</svg>
								</button>
							</div>
						) }

						{ /* Pagination */ }
						{ effective.pagination && (
							<div className="gwx-carousel__pagination">
								{ Array.from( { length: displaySnaps || slideCount } ).map( ( _, i ) => {
									const isActive = i === selectedSnap;
									const customColors = isActive ? paginationCustomColors.active : paginationCustomColors.inactive;

									const colorSlug = isActive ? attributes.paginationColor : attributes.paginationInactiveColor;
									const bgSlug = isActive ? attributes.paginationBackgroundColor : attributes.paginationInactiveBackgroundColor;
									const borderSlug = isActive ? attributes.paginationBorderColor : attributes.paginationInactiveBorderColor;

									const hasTextColor = !! colorSlug || !! customColors.color;
									const hasBackground = isNumberPagination && ( !! bgSlug || !! customColors.backgroundColor );
									const hasBorderColor = isNumberPagination && ( !! borderSlug || !! customColors.borderColor );

									if ( paginationStyleName === 'number' ) {
										return (
											<button
												key={ i }
												type="button"
												className={ clsx(
													'gwx-carousel__pagination-item',
													'gwx-carousel__pagination-item--number',
													'wp-element-button',
													{
														'is-active': isActive,
														'has-text-color': hasTextColor,
														'has-background': hasBackground,
														'has-border-color': hasBorderColor,
														[ `has-${ colorSlug }-color` ]: !! colorSlug,
														[ `has-${ bgSlug }-background-color` ]: !! bgSlug,
														[ `has-${ borderSlug }-border-color` ]: !! borderSlug,
													}
												) }
												style={ {
													color: customColors.color,
													backgroundColor: customColors.backgroundColor,
													borderColor: customColors.borderColor,
												} }
												onClick={ () => emblaRef.current?.scrollTo( i ) }
											>
												{ i + 1 }
											</button>
										);
									}

									const pStyle =
										PAGINATION_STYLES[ paginationStyleName ] ||
										PAGINATION_STYLES.circleOutline;
									return (
										<button
											key={ i }
											type="button"
											className={ clsx( 'gwx-carousel__pagination-item', {
												'is-active': isActive,
												'has-text-color': hasTextColor,
												[ `has-${ colorSlug }-color` ]: !! colorSlug,
											} ) }
											style={ {
												color: customColors.color,
											} }
											onClick={ () => emblaRef.current?.scrollTo( i ) }
											dangerouslySetInnerHTML={
												pStyle?.svg ? { __html: pStyle.svg } : undefined
											}
										/>
									);
								} ) }
							</div>
						) }

						{ /* Progress bar */ }
						{ effective.progressBar && (
							<div
								className={ clsx( 'gwx-carousel__progress', {
									'has-background': !! attributes.progressBackgroundColor || !! attributes.customProgressBackgroundColor,
									[ `has-${ attributes.progressBackgroundColor }-background-color` ]: !! attributes.progressBackgroundColor,
								} ) }
								style={ {
									backgroundColor: attributes.customProgressBackgroundColor || undefined,
								} }
							>
								<div
									className={ clsx( 'gwx-carousel__progress-bar', {
										'has-background': !! attributes.progressColor || !! attributes.customProgressColor,
										[ `has-${ attributes.progressColor }-background-color` ]: !! attributes.progressColor,
									} ) }
									style={ {
										width: progressWidth,
										backgroundColor: attributes.customProgressColor || undefined,
									} }
								/>
							</div>
						) }
					</div>
				</div>
			</section>
		</>
	);
}

export default withColors( {
	progressColor: 'color',
	progressBackgroundColor: 'backgroundColor',
	paginationColor: 'color',
	paginationBackgroundColor: 'color',
	paginationBorderColor: 'color',
	paginationInactiveColor: 'color',
	paginationInactiveBackgroundColor: 'color',
	paginationInactiveBorderColor: 'color',
	arrowsColor: 'color',
	arrowsBackgroundColor: 'backgroundColor',
	arrowsBorderColor: 'color',
	counterColor: 'color',
} )( Edit );
